import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Zap, 
  Phone, 
  Mail, 
  CheckCircle2, 
  Shield, 
  Award, 
  Clock, 
  ArrowRight, 
  ArrowLeft,
  Home,
  Building2,
  Car,
  Lightbulb,
  Cpu,
  FileCheck,
  Star,
  Users,
  AlertCircle,
  Download,
  Calendar,
  X
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import WhatsAppButton from '../components/WhatsAppButton';
import EBookModal from '../components/EBookModal';
import ConsultationModal from '../components/ConsultationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ELECTRICAL_EBOOK = {
  title: "Homeowner's Electrical Safety Guide",
  description: "Everything Ottawa homeowners need to know about electrical safety, upgrades, and saving money on your next electrical project.",
  chapters: [
    "Signs your home needs rewiring",
    "Panel upgrade: when and why",
    "EV charger installation checklist",
    "How to choose a licensed electrician",
    "Ontario electrical code basics for homeowners",
    "Energy-saving electrical upgrades"
  ]
};

const ELECTRICAL_PROJECT_TYPES = [
  { value: "panel-upgrade", label: "Panel Upgrade" },
  { value: "residential-wiring", label: "Residential Wiring / Rewiring" },
  { value: "ev-charger", label: "EV Charger Installation" },
  { value: "lighting", label: "Lighting Installation" },
  { value: "commercial", label: "Commercial Electrical" },
  { value: "code-compliance", label: "Code Compliance / Inspection" },
  { value: "smart-home", label: "Smart Home Wiring" },
  { value: "other", label: "Other" }
];

const electricalServices = [
  {
    id: 1,
    icon: Home,
    title: "Residential Wiring & Rewiring",
    description: "Complete home wiring for new builds, renovations, and full rewiring of older homes to meet current electrical codes. Safe, reliable power for your family.",
    features: ["New construction wiring", "Whole-home rewiring", "Aluminum to copper upgrades", "Dedicated circuits"]
  },
  {
    id: 2,
    icon: Zap,
    title: "Panel Upgrades & Installations",
    description: "Upgrade your electrical panel to handle modern power demands. From 100A to 200A upgrades and new panel installations for homes and businesses.",
    features: ["100A to 200A upgrades", "Sub-panel installations", "Breaker replacements", "Load balancing"]
  },
  {
    id: 3,
    icon: Car,
    title: "EV Charger Installations",
    description: "Future-proof your home or business with professional Level 2 EV charger installations. We handle permits, wiring, and setup — drive electric with confidence.",
    features: ["Level 2 charger installation", "Tesla Wall Connector", "Commercial fleet charging", "Permit handling"]
  },
  {
    id: 4,
    icon: Lightbulb,
    title: "Lighting Installations",
    description: "Transform your space with expert lighting design and installation. Pot lights, LED upgrades, under-cabinet lighting, and outdoor landscape lighting.",
    features: ["Pot light installation", "LED upgrades & retrofits", "Under-cabinet lighting", "Outdoor & landscape lighting"]
  },
  {
    id: 5,
    icon: Building2,
    title: "Commercial Electrical",
    description: "Reliable electrical solutions for offices, retail spaces, and commercial properties. From tenant fit-outs to complete electrical systems.",
    features: ["Office & retail wiring", "Tenant improvements", "Emergency lighting", "Code upgrades"]
  },
  {
    id: 6,
    icon: FileCheck,
    title: "Code Compliance & ESA Inspections",
    description: "Ensure your property meets all Ontario electrical safety standards. We handle ESA inspections, code compliance upgrades, and deficiency corrections.",
    features: ["ESA inspection prep", "Code violation corrections", "Safety audits", "Certificate of inspection"]
  },
  {
    id: 7,
    icon: Cpu,
    title: "Smart Home Wiring",
    description: "Modern living requires modern wiring. Smart switches, automated lighting, home theatre pre-wiring, and structured cabling for the connected home.",
    features: ["Smart switch installation", "Home automation wiring", "Structured cabling", "Home theatre pre-wire"]
  }
];

const whyChooseUs = [
  { icon: Shield, title: "Licensed & Insured", description: "ECRA/ESA Licensed #7018724. Fully insured for your peace of mind." },
  { icon: Award, title: "Master Electrician", description: "Certified Master Electrician with years of residential and commercial experience." },
  { icon: Clock, title: "On-Time Service", description: "We respect your schedule. Arrive on time, complete on time, every time." },
  { icon: CheckCircle2, title: "Code Compliant", description: "Every job meets or exceeds Ontario Electrical Safety Code standards." },
  { icon: Users, title: "Trusted by Ottawa", description: "5-star rated service with a growing list of happy homeowners and businesses." },
  { icon: Star, title: "Quality Guaranteed", description: "We stand behind our work with warranty coverage on all installations." }
];

const electricalFaqs = [
  {
    id: 1,
    question: "How do I know if I need a panel upgrade?",
    answer: "Common signs include frequently tripping breakers, flickering lights, a fuse box instead of breakers, or planning to add major appliances like an EV charger or hot tub. If your home has a 60A or 100A panel and you're experiencing issues, an upgrade to 200A is usually recommended."
  },
  {
    id: 2,
    question: "Do I need a permit for electrical work?",
    answer: "In Ontario, most electrical work requires an ESA (Electrical Safety Authority) permit and inspection. This includes panel upgrades, new circuits, rewiring, and EV charger installations. We handle all permit applications and coordinate inspections for you."
  },
  {
    id: 3,
    question: "How long does an EV charger installation take?",
    answer: "A typical Level 2 EV charger installation takes 3-6 hours depending on the distance from your panel to the charging location and whether a panel upgrade is needed. We'll provide a clear timeline during your free estimate."
  },
  {
    id: 4,
    question: "What areas do you serve?",
    answer: "We serve Ottawa and the surrounding areas including Kanata, Barrhaven, Orleans, Nepean, Stittsville, Manotick, and the greater Ottawa-Gatineau region."
  },
  {
    id: 5,
    question: "Are your electricians licensed?",
    answer: "Absolutely. We are a fully licensed electrical contractor (ECRA/ESA #7018724) with certified Master Electricians on staff. All our work is inspected and approved by the Electrical Safety Authority."
  },
  {
    id: 6,
    question: "Do you offer free estimates?",
    answer: "Yes! We offer free, no-obligation estimates for all electrical projects. Contact us to schedule a visit and we'll provide a detailed written quote."
  }
];

const Electrical = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactStatus, setContactStatus] = useState('');
  const [contactError, setContactError] = useState('');
  const [isEBookOpen, setIsEBookOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [showEBookPopup, setShowEBookPopup] = useState(false);

  // Show e-book popup after 30 seconds on desktop
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      const timer = setTimeout(() => {
        setShowEBookPopup(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    setContactError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        ...contactForm,
        source: 'electrical_page'
      });
      if (res.data.success) {
        setContactStatus('sent');
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error(res.data.error || 'Failed to send');
      }
    } catch (err) {
      setContactStatus('error');
      setContactError('Something went wrong. Please call us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="electrical-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[rgb(0,55,32)]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
              data-testid="electrical-back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </a>
            <div className="h-5 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#FBBC04]" />
              <span className="text-white font-bold text-sm sm:text-base">MM Electrical</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#services" className="text-white/70 hover:text-white transition-colors">Services</a>
            <a href="#why-us" className="text-white/70 hover:text-white transition-colors">Why Us</a>
            <a href="#faq" className="text-white/70 hover:text-white transition-colors">FAQ</a>
            <a href="#contact" className="text-white/70 hover:text-white transition-colors">Contact</a>
          </div>
          <a href="tel:613-295-9098" className="flex items-center gap-2 text-white font-semibold text-sm" data-testid="electrical-phone-nav">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">613-295-9098</span>
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 bg-[rgb(0,55,32)] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FBBC04] rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#4A7F25] rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-[#FBBC04]" />
              <span className="text-white/80 text-sm font-medium">ECRA/ESA Licensed #7018724</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="electrical-hero-title">
              Ottawa's Trusted
              <span className="block text-[#FBBC04]">Electrical Contractor</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mb-8 max-w-2xl leading-relaxed">
              Licensed Master Electrician serving Ottawa homes and businesses. 
              From panel upgrades to EV chargers — safe, code-compliant electrical work you can count on.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsEBookOpen(true)}
                className="bg-[#FBBC04] text-[rgb(0,55,32)] font-bold h-14 px-8 rounded-full text-lg hover:bg-[#F5A623] transition-colors w-full sm:w-auto"
                data-testid="electrical-hero-ebook"
              >
                <Download className="w-5 h-5 mr-2" />
                Free Electrical Guide
              </Button>
              <Button
                onClick={() => setIsConsultationOpen(true)}
                variant="outline"
                className="border-white/30 text-white h-14 px-8 rounded-full text-lg hover:bg-white/10 w-full sm:w-auto"
                data-testid="electrical-hero-consultation"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[rgb(0,45,26)] py-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12 text-center">
            <div>
              <div className="text-2xl font-bold text-[#FBBC04]">ESA</div>
              <div className="text-white/50 text-xs mt-1">Licensed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">5.0</div>
              <div className="text-white/50 text-xs mt-1 flex items-center gap-1">
                <Star className="w-3 h-3 fill-[#FBBC04] text-[#FBBC04]" /> Google Rating
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-white/50 text-xs mt-1">Available</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">100%</div>
              <div className="text-white/50 text-xs mt-1">Code Compliant</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">Free</div>
              <div className="text-white/50 text-xs mt-1">Estimates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4" data-testid="electrical-services-title">
              Our Electrical Services
            </h2>
            <p className="text-lg text-[rgb(131,146,140)] max-w-2xl mx-auto">
              Professional electrical solutions for residential and commercial properties across Ottawa
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {electricalServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.id}
                  className="p-6 bg-white border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                  data-testid={`electrical-service-${service.id}`}
                >
                  <div className="w-12 h-12 bg-[rgba(45,80,22,0.1)] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[rgb(0,55,32)] transition-colors">
                    <Icon className="w-6 h-6 text-[var(--accent-primary)] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[rgb(0,55,32)] mb-2">{service.title}</h3>
                  <p className="text-[rgb(131,146,140)] text-sm leading-relaxed mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[rgb(14,15,12)]">
                        <CheckCircle2 className="w-4 h-4 text-[var(--accent-primary)] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-20 px-4 sm:px-6 bg-[rgba(0,0,0,0.02)]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4" data-testid="electrical-why-title">
              Why Choose MM Electrical
            </h2>
            <p className="text-lg text-[rgb(131,146,140)] max-w-2xl mx-auto">
              Licensed, insured, and committed to doing it right the first time
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-6 bg-white rounded-xl border border-[rgba(0,0,0,0.08)] hover:shadow-md transition-shadow"
                  data-testid={`why-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[rgb(0,55,32)] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[rgb(0,55,32)] mb-1">{item.title}</h3>
                    <p className="text-sm text-[rgb(131,146,140)] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4 sm:px-6 bg-[rgb(0,55,32)]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Need Electrical Work Done Right?
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation estimate from a licensed Master Electrician. 
            We'll assess your needs and provide an honest, upfront quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsConsultationOpen(true)}
              className="bg-[#FBBC04] text-[rgb(0,55,32)] font-bold h-14 px-8 rounded-full text-lg hover:bg-[#F5A623] w-full sm:w-auto"
            >
              Book Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a href="tel:613-295-9098">
              <Button variant="outline" className="border-white/30 text-white h-14 px-8 rounded-full text-lg hover:bg-white/10 w-full sm:w-auto">
                <Phone className="w-5 h-5 mr-2" />
                613-295-9098
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4" data-testid="electrical-faq-title">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[rgb(131,146,140)]">
              Common questions about our electrical services
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {electricalFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`faq-${faq.id}`}
                className="bg-white border border-[rgba(0,0,0,0.1)] rounded-xl px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-left font-semibold text-[rgb(0,55,32)] py-5 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[rgb(131,146,140)] leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 bg-[rgba(0,0,0,0.02)]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4" data-testid="electrical-contact-title">
                Get Your Free Estimate
              </h2>
              <p className="text-lg text-[rgb(131,146,140)] mb-8 leading-relaxed">
                Ready to get started? Fill out the form or contact us directly. 
                We'll get back to you within 24 hours with a detailed quote.
              </p>

              <div className="space-y-6 mb-8">
                <a href="tel:613-295-9098" className="flex items-center gap-4 group" data-testid="electrical-contact-phone">
                  <div className="w-12 h-12 bg-[rgb(0,55,32)] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-[rgb(131,146,140)]">Call or Text</div>
                    <div className="font-bold text-[rgb(0,55,32)]">613-295-9098</div>
                  </div>
                </a>
                <a href="mailto:MMelec.89@gmail.com" className="flex items-center gap-4 group" data-testid="electrical-contact-email">
                  <div className="w-12 h-12 bg-[rgb(0,55,32)] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-[rgb(131,146,140)]">Email Us</div>
                    <div className="font-bold text-[rgb(0,55,32)]">MMelec.89@gmail.com</div>
                  </div>
                </a>
              </div>

              <div className="bg-white p-6 rounded-xl border border-[rgba(0,0,0,0.1)]">
                <h3 className="font-bold text-[rgb(0,55,32)] mb-3">Service Areas</h3>
                <p className="text-[rgb(131,146,140)] text-sm leading-relaxed">
                  Ottawa, Kanata, Barrhaven, Orleans, Nepean, Stittsville, 
                  Manotick, Gloucester, Vanier, and the greater Ottawa-Gatineau region.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl shadow-sm">
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="elec-name" className="text-[rgb(0,55,32)] font-semibold">Full Name *</Label>
                  <Input
                    id="elec-name"
                    placeholder="Your name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                    className="mt-1 h-12 border-[rgba(0,0,0,0.15)] focus:border-[var(--accent-primary)]"
                    data-testid="electrical-form-name"
                  />
                </div>
                <div>
                  <Label htmlFor="elec-email" className="text-[rgb(0,55,32)] font-semibold">Email *</Label>
                  <Input
                    id="elec-email"
                    type="email"
                    placeholder="your@email.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                    className="mt-1 h-12 border-[rgba(0,0,0,0.15)] focus:border-[var(--accent-primary)]"
                    data-testid="electrical-form-email"
                  />
                </div>
                <div>
                  <Label htmlFor="elec-phone" className="text-[rgb(0,55,32)] font-semibold">Phone Number</Label>
                  <Input
                    id="elec-phone"
                    type="tel"
                    placeholder="(613) 000-0000"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="mt-1 h-12 border-[rgba(0,0,0,0.15)] focus:border-[var(--accent-primary)]"
                    data-testid="electrical-form-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="elec-message" className="text-[rgb(0,55,32)] font-semibold">Tell Us About Your Project *</Label>
                  <Textarea
                    id="elec-message"
                    placeholder="Describe the electrical work you need..."
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    rows={4}
                    className="mt-1 border-[rgba(0,0,0,0.15)] focus:border-[var(--accent-primary)]"
                    data-testid="electrical-form-message"
                  />
                </div>
                {contactError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {contactError}
                  </div>
                )}
                {contactStatus === 'sent' && (
                  <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
                    <CheckCircle2 className="w-4 h-4" />
                    Thank you! We'll get back to you within 24 hours.
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={contactStatus === 'sending'}
                  className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white h-14 rounded-xl text-lg font-bold hover:scale-[1.01] transition-transform"
                  data-testid="electrical-form-submit"
                >
                  {contactStatus === 'sending' ? 'Sending...' : 'Request Free Estimate'}
                </Button>
                <p className="text-xs text-[rgb(175,183,180)] text-center">
                  We respect your privacy. <a href="/privacy-policy" className="underline hover:text-[rgb(0,55,32)]">Privacy Policy</a>
                </p>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[rgb(0,55,32)] text-white py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-[#FBBC04]" />
                <span className="font-bold text-lg">MM Electrical</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Licensed electrical contractor serving Ottawa and surrounding areas. 
                ECRA/ESA #7018724.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="#services" className="block text-white/60 hover:text-white transition-colors">Services</a>
                <a href="#why-us" className="block text-white/60 hover:text-white transition-colors">Why Choose Us</a>
                <a href="#faq" className="block text-white/60 hover:text-white transition-colors">FAQ</a>
                <a href="#contact" className="block text-white/60 hover:text-white transition-colors">Contact</a>
                <a href="/construction" className="block text-white/60 hover:text-white transition-colors">Construction Services</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-white/60">
                <p>Phone: 613-295-9098</p>
                <p>Email: MMelec.89@gmail.com</p>
                <p>Service Area: Ottawa & Region</p>
                <p className="text-[#FBBC04] font-semibold">ECRA/ESA #7018724</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <p>&copy; 2026 Mat Moffatt Renovations LTD. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
              <span>|</span>
              <a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <EBookModal
        isOpen={isEBookOpen}
        onClose={() => setIsEBookOpen(false)}
        source="electrical_page"
        title={ELECTRICAL_EBOOK.title}
        description={ELECTRICAL_EBOOK.description}
        chapters={ELECTRICAL_EBOOK.chapters}
      />
      <ConsultationModal
        isOpen={isConsultationOpen}
        onClose={() => setIsConsultationOpen(false)}
        source="electrical_page"
        projectTypes={ELECTRICAL_PROJECT_TYPES}
      />

      {/* E-Book Popup (bottom-left, 30s delay) */}
      {showEBookPopup && (
        <div className="fixed bottom-6 left-6 z-50 max-w-sm animate-in slide-in-from-left-5 duration-500" data-testid="electrical-ebook-popup">
          <Card className="p-5 bg-white border-[rgba(0,0,0,0.15)] rounded-xl shadow-2xl relative">
            <button
              onClick={() => setShowEBookPopup(false)}
              className="absolute top-3 right-3 text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors"
              data-testid="electrical-popup-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[rgb(0,55,32)] rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#FBBC04]" />
              </div>
              <div>
                <h4 className="font-bold text-[rgb(0,55,32)] text-sm">{ELECTRICAL_EBOOK.title}</h4>
                <p className="text-xs text-[rgb(131,146,140)] mt-1 mb-3">Free guide for Ottawa homeowners</p>
                <Button
                  onClick={() => { setShowEBookPopup(false); setIsEBookOpen(true); }}
                  size="sm"
                  className="bg-[rgb(0,55,32)] text-white rounded-full text-xs h-8 px-4 hover:bg-[rgb(0,70,40)]"
                  data-testid="electrical-popup-download"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Free
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Electrical;
